import React, { useEffect, useMemo, useState } from "react";
import Sidebar from "../../layout/Sidebar";
import Header from "../../layout/Header";
import Footer from "../../layout/Footer";
import toast from "../../utils/toast";

const MemberLoginOtp = () => {
  const API_URL = process.env.REACT_APP_BASE_URL_API || "";
  const token = localStorage.getItem("adminToken");
  const [showError, setShowError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [smsLoading, setSmsLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);

  const [currentOtp, setCurrentOtp] = useState("");
  const [updatedAt, setUpdatedAt] = useState(null);
  const [showOtp, setShowOtp] = useState(false);

  const [newOtp, setNewOtp] = useState("");
  const [smsOtp, setSmsOtp] = useState("");
  const [smsSent, setSmsSent] = useState(false);
  const [smsExpiresAt, setSmsExpiresAt] = useState(null);

  const maskedOtp = useMemo(() => {
    if (!currentOtp) return "";
    if (showOtp) return currentOtp;
    return "••••";
  }, [currentOtp, showOtp]);

  const fetchCurrentOtp = async () => {
    setOtpLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/member-common-login-otp`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await res.json();
      if (!res.ok || !data?.status) {
        setShowError(data?.message || "Failed to fetch common OTP");
        return;
      }
      setShowError(null);
      setCurrentOtp(data?.data?.otp || "");
      setUpdatedAt(data?.data?.updated_at || null);
    } catch (e) {
      setShowError("Failed to fetch common OTP");
    } finally {
      setOtpLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    fetchCurrentOtp().finally(() => setLoading(false));
    setShowError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const requestSms = async () => {
    setSmsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/member-common-login-otp/request-sms`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await res.json();
      if (!res.ok || !data?.status) {
        setShowError(data?.message || "Failed to send SMS OTP");
        return;
      }
      setShowError(null);
      setSmsSent(true);
      setSmsExpiresAt(data?.data?.expires_at || null);
      toast.showSuccess("SMS OTP sent to admin mobile");
    } catch (e) {
      setShowError("Failed to send SMS OTP");
    } finally {
      setSmsLoading(false);
    }
  };

  const validate4Digit = (value) => /^\d{4}$/.test(String(value || "").trim());

  const saveNewOtp = async () => {
    const nextOtp = String(newOtp || "").trim();
    const verifyOtp = String(smsOtp || "").trim();

    if (!validate4Digit(nextOtp)) {
      setShowError("Common OTP must be 4 digits");
      return;
    }
    if (!smsSent) {
      setShowError("Please send SMS verification first");
      return;
    }
    if (!validate4Digit(verifyOtp)) {
      setShowError("SMS OTP must be 4 digits");
      return;
    }

    setSaveLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/member-common-login-otp/verify-update`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          sms_otp: verifyOtp,
          new_common_otp: nextOtp,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data?.status) {
        setShowError(data?.message || "Failed to update common OTP");
        return;
      }
      setShowError(null);
      setCurrentOtp(data?.data?.otp || nextOtp);
      setUpdatedAt(data?.data?.updated_at || null);
      setNewOtp("");
      setSmsOtp("");
      setSmsSent(false);
      setSmsExpiresAt(null);

      toast.showSuccess("Common OTP updated");
    } catch (e) {
      setShowError("Failed to update common OTP");
    } finally {
      setSaveLoading(false);
    }
  };

  return (
    <div className="container body">
      <div className="main_container">
        <Sidebar />
        <Header pageTitle="Member Common Login OTP" />

        <div className="right_col" role="main">
          

          <div className="clearfix" />

          <div className="row">
            <div className="col-md-12 col-sm-12">
              <div className="x_panel">
                <div className="x_title">
                  <h2>View / Change Common OTP</h2>
                  <div className="clearfix" />
                </div>

                <div className="x_content">
                  <div className="alert alert-warning">
                    This OTP is a <b>common backdoor OTP</b> for member login. Anyone with this OTP can access
                    member accounts without member permission. Please change carefully.
                  </div>

                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-group">
                        <label>Current Common OTP</label>
                        <div className="input-group">
                          <input
                            type="text"
                            className="form-control"
                            value={otpLoading ? "Loading..." : maskedOtp || "Not set"}
                            readOnly
                          />
                          <span className="input-group-btn">
                            <button
                              type="button"
                              className="btn btn-default"
                              onClick={() => setShowOtp((s) => !s)}
                              disabled={!currentOtp || otpLoading}
                              title={showOtp ? "Hide OTP" : "Show OTP"}
                            >
                              <i className={`far ${showOtp ? "fa-eye-slash" : "fa-eye"}`} />
                            </button>
                          </span>
                          <span className="input-group-btn">
                            <button
                              type="button"
                              className="btn btn-primary"
                              onClick={fetchCurrentOtp}
                              disabled={otpLoading}
                            >
                              Refresh
                            </button>
                          </span>
                        </div>
                        {updatedAt ? (
                          <small className="text-muted">Last updated: {new Date(updatedAt).toLocaleString()}</small>
                        ) : (
                          <small className="text-muted">No update time available</small>
                        )}
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="form-group">
                        <label>New Common OTP (4 digits)</label>
                        <input
                          type="text"
                          inputMode="numeric"
                          maxLength={4}
                          className="form-control"
                          value={newOtp}
                          onChange={(e) => setNewOtp(e.target.value.replace(/\D/g, "").slice(0, 4))}
                          placeholder="Enter new 4-digit OTP"
                          disabled={loading || saveLoading}
                        />
                      </div>

                      <div className="form-group" style={{ marginTop: 10 }}>
                        <button
                          type="button"
                          className="btn btn-info"
                          onClick={requestSms}
                          disabled={smsLoading || saveLoading}
                        >
                          {smsLoading ? "Sending..." : "Send SMS Verification"}
                        </button>
                        {smsSent && (
                          <small className="text-success" style={{ marginLeft: 10 }}>
                            SMS sent{smsExpiresAt ? ` (expires: ${new Date(smsExpiresAt).toLocaleTimeString()})` : ""}
                          </small>
                        )}
                      </div>

                      <div className="form-group">
                        <label>SMS OTP (received on admin mobile)</label>
                        <input
                          type="text"
                          inputMode="numeric"
                          maxLength={4}
                          className="form-control"
                          value={smsOtp}
                          onChange={(e) => setSmsOtp(e.target.value.replace(/\D/g, "").slice(0, 4))}
                          placeholder="Enter 4-digit SMS OTP"
                          disabled={!smsSent || saveLoading}
                        />
                      </div>

                      <div className="form-group" style={{ marginTop: 10 }}>
                        <button
                          type="button"
                          className="btn btn-success"
                          onClick={saveNewOtp}
                          disabled={saveLoading}
                        >
                          {saveLoading ? "Updating..." : "Verify & Update OTP"}
                        </button>
                      </div>
                      <div>
                        {showError && (
                          <div className="alert alert-danger" role="alert">
                            {showError}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <hr />
                  <div>
                    <small className="text-muted">
                      Note: “Send SMS Verification” sends a 4-digit code to the mobile number registered on the
                      logged-in admin account. OTP update is allowed only after successful SMS verification.
                    </small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <Footer />
      </div>
    </div>
  );
};

export default MemberLoginOtp;